import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './IncluirJackpot.css';

/**
 * Página para Admin gerenciar jackpot
 */

const IncluirJackpot = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadaAberta, setRodadaAberta] = useState(null);
  const [jogadoresConfirmados, setJogadoresConfirmados] = useState([]);
  const [jackpotAtivado, setJackpotAtivado] = useState(false);
  const [jackpotGanhadorId, setJackpotGanhadorId] = useState('');
  const [arrecadacao, setArrecadacao] = useState(0);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      const rodadasRes = await apiClient.get('/rodadas');
      const rodadas = Array.isArray(rodadasRes.data) ? rodadasRes.data : [];
      const aberta = rodadas.find(r => r.status === 'aberta');

      if (!aberta) {
        setError('Não há rodada aberta. Abra uma rodada primeiro.');
        setLoading(false);
        return;
      }

      setRodadaAberta(aberta);
      setJackpotAtivado(aberta.jackpot_ativado || false);
      setJackpotGanhadorId(aberta.jackpot_ganhador_id || '');

      // Carregar confirmações
      const confirmacoesRes = await apiClient.get(`/rodadas/${aberta.id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      setJogadoresConfirmados(confirmacoes);

      // Carregar arrecadação se jackpot ativado
      if (aberta.jackpot_ativado && aberta.jackpot_ganhador_id) {
        const arrecRes = await apiClient.get(`/rodadas/${aberta.id}/jackpot-arrecadacao`);
        setArrecadacao(arrecRes.data.arrecadacao || 0);
      }

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async (e) => {
    e.preventDefault();

    try {
      setSalvando(true);
      setError('');

      await apiClient.put(`/rodadas/${rodadaAberta.id}/jackpot`, {
        jackpot_ativado: jackpotAtivado,
        jackpot_ganhador_id: jackpotAtivado && jackpotGanhadorId ? jackpotGanhadorId : null,
      });

      // Recarregar arrecadação
      if (jackpotAtivado && jackpotGanhadorId) {
        const arrecRes = await apiClient.get(`/rodadas/${rodadaAberta.id}/jackpot-arrecadacao`);
        setArrecadacao(arrecRes.data.arrecadacao || 0);
      } else {
        setArrecadacao(0);
      }

      alert('Jackpot atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar jackpot:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar jackpot');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodadaAberta) {
    return (
      <div className="container">
        <div className="error">Não há rodada aberta. Abra uma rodada primeiro.</div>
        <button onClick={() => navigate('/rodadas')} className="btn btn-primary">
          Ir para Rodadas
        </button>
      </div>
    );
  }

  return (
    <div className="container incluir-jackpot-container">
      <div className="page-header">
        <h1>🎰 Gerenciar Jackpot</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodadaAberta.data).toLocaleDateString('pt-BR')}</p>
        <p><strong>Valor por jogador:</strong> R$ 10,00 (exceto o ganhador)</p>
      </div>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleSalvar} className="jackpot-form">
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={jackpotAtivado}
              onChange={(e) => {
                setJackpotAtivado(e.target.checked);
                if (!e.target.checked) {
                  setJackpotGanhadorId('');
                }
              }}
            />
            {' '}Jackpot Ativado
          </label>
          <small>Se marcado, todos os jogadores pagarão R$ 10,00 (exceto o ganhador)</small>
        </div>

        {jackpotAtivado && (
          <>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={!!jackpotGanhadorId}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setJackpotGanhadorId('');
                    }
                  }}
                />
                {' '}Alguém ganhou o Jackpot
              </label>
            </div>

            {jackpotGanhadorId && (
              <div className="form-group">
                <label>Ganhador do Jackpot *</label>
                <select
                  value={jackpotGanhadorId}
                  onChange={(e) => setJackpotGanhadorId(e.target.value)}
                  required
                >
                  <option value="">Selecione o ganhador...</option>
                  {jogadoresConfirmados.map(c => (
                    <option key={c.jogador_id} value={c.jogador_id}>
                      {c.jogador?.nome || 'Jogador'}
                    </option>
                  ))}
                </select>
                <small>O ganhador não paga os R$ 10,00 do jackpot</small>
              </div>
            )}
          </>
        )}

        {arrecadacao > 0 && (
          <div className="arrecadacao-box">
            <h3>💰 Arrecadação do Jackpot</h3>
            <p className="valor-arrecadacao">R$ {arrecadacao.toFixed(2)}</p>
            <small>
              {jogadoresConfirmados.length - 1} jogador(es) pagaram R$ 10,00 cada
            </small>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" disabled={salvando} className="btn btn-primary">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IncluirJackpot;
